import React from 'react';
import { useLocation } from 'react-router-dom';

import { useLazyViewSecretQuery } from '@/api/secretApi';
import { SECRET_TYPES } from '@/types';
import { decryptFile, decryptSecret, parseRevealUrl, validateUrlParams } from '@/utils';

import { getCaptchaToken } from '@/utils/captcha';
import { reportApiFailure, reportDeadEnd } from '@/utils/monitoring';
import {
    buildViewSecretRequest,
    getEmailVerificationState,
    normalizeSecretType,
    normalizeUrlPinType,
    parseApiResponse,
    resolvePinTypeFromDetails,
} from '@/utils/secretHelpers';

import type {
    ApiResponse,
    CredentialsData,
    DecryptedSecret,
    PinType,
    SecretDetails,
    SecretStatus,
    UrlParams,
} from '@/types';

const useUrlParams = (): UrlParams | null => {
    const { pathname, hash } = useLocation();
    return React.useMemo(() => {
        const params = parseRevealUrl(pathname, hash);
        return validateUrlParams(params) ? params : null;
    }, [pathname, hash]);
};
export const useRevealSecret = () => {
    const urlParams = useUrlParams();
    const [trigger, { data, error, isLoading, isFetching }] = useLazyViewSecretQuery();
    const [status, setStatus] = React.useState<SecretStatus>('start');
    const [decryptedSecret, setDecryptedSecret] = React.useState<DecryptedSecret | null>(null);
    const [pinError, setPinError] = React.useState<string | null>(null);
    const [serverError, setServerError] = React.useState<string | null>(null);
    const [emailGuess, setEmailGuess] = React.useState<string>('');
    const [isDecrypting, setIsDecrypting] = React.useState<boolean>(false);
    const pinTypeFromUrl = React.useMemo(() => normalizeUrlPinType(urlParams?.pinType), [urlParams?.pinType]);
    const pinTypeFromApi = React.useMemo(() => resolvePinTypeFromDetails(data?.details), [data?.details]);
    const pinType = React.useMemo<PinType>(() => {
        return pinTypeFromApi ?? pinTypeFromUrl ?? 'static';
    }, [pinTypeFromApi, pinTypeFromUrl]);
    const emailVerification = React.useMemo(() => getEmailVerificationState(data?.details), [data?.details]);
    const fetchSecret = React.useCallback(
        async (pinValue?: string, emailGuessValue?: string): Promise<boolean> => {
            if (!urlParams) {
                setServerError('Invalid reveal URL.');
                setPinError(null);
                setStatus('error');
                return false;
            }
            setPinError(null);
            setServerError(null);
            const request = buildViewSecretRequest(urlParams.secretId, pinType, pinValue, emailGuessValue);

            // The API scores a reCAPTCHA token before it will answer the e-mail question, and
            // refuses the request outright without one.
            if (request.emailGuess || request.emailVerificationCode) {
                request.captchaToken = await getCaptchaToken('emailVerification');
            }
            try {
                const result = await trigger(request).unwrap();
                const parsed = parseApiResponse(result, false);
                if (parsed.pinError) {
                    setPinError(parsed.pinError);
                }
                if (parsed?.serverMessage) {
                    setServerError(parsed?.serverMessage);
                }
                setStatus(parsed.status);
                return parsed.status === 'ready';
            } catch (requestError) {
                const apiError = requestError as {
                    data?: ApiResponse<SecretDetails>;
                    error?: string;
                    status?: number | string;
                };
                if (apiError.data) {
                    const parsed = parseApiResponse(apiError.data, true);
                    if (parsed.pinError) {
                        setPinError(parsed.pinError);
                    }
                    if (parsed?.serverMessage) {
                        setServerError(parsed?.serverMessage);
                    } else if (parsed.message) {
                        setServerError(parsed.message);
                    }
                    if (parsed.status === 'error') {
                        reportDeadEnd('reveal', `${apiError.status ?? 'no status'}: ${parsed.message ?? ''}`);
                    }

                    setStatus(parsed.status);
                    return false;
                }
                const fallbackMessage =
                    typeof apiError.error === 'string' && apiError.error.trim()
                        ? apiError.error
                        : 'Something went wrong while loading the secret.';

                // Nothing here was understood, so the reader gets the generic screen and their
                // secret stays shut. They will not report it, so this is how we hear about it.
                reportApiFailure({
                    endpoint: '/secret/view',
                    status: apiError.status ?? 'unknown',
                    message: typeof apiError.error === 'string' ? apiError.error : undefined,
                });

                setServerError(fallbackMessage);
                setStatus('error');
                return false;
            }
        },
        [urlParams, trigger, pinType]
    );
    const handleView = React.useCallback(async (): Promise<void> => {
        if (!urlParams) {
            setServerError('Invalid reveal URL.');
            setStatus('error');
            return;
        }
        if (urlParams.pinType) {
            const type = normalizeUrlPinType(urlParams.pinType);
            setServerError(null);
            setPinError(null);
            setStatus(type === 'email' ? 'pin_email_guess' : 'pin_required');
            return;
        }
        await fetchSecret();
    }, [urlParams, fetchSecret]);
    const submitPin = React.useCallback(
        async (pinValue: string): Promise<void> => {
            await fetchSecret(pinValue);
        },
        [fetchSecret]
    );
    const submitEmailGuess = React.useCallback(
        async (email: string | undefined): Promise<boolean> => {
            const normalizedEmail = typeof email === 'string' ? email : '';
            setEmailGuess(normalizedEmail);
            return await fetchSecret(undefined, normalizedEmail);
        },
        [fetchSecret]
    );
    const submitEmailCode = React.useCallback(
        async (code: string | undefined): Promise<boolean> => {
            const normalizedCode = typeof code === 'string' ? code : '';
            return await fetchSecret(normalizedCode, emailGuess);
        },
        [fetchSecret, emailGuess]
    );
    const revealSecret = React.useCallback(async (): Promise<void> => {
        if (!urlParams || !data?.details) {
            return;
        }
        setIsDecrypting(true);
        setServerError(null);
        try {
            const details = data.details;
            const secretType = normalizeSecretType(details.type);
            if (secretType === SECRET_TYPES.FILE && details.download && typeof details.download === 'string') {
                setDecryptedSecret({
                    type: secretType,
                    content: details.download,
                    publicDescription: details.publicDescription,
                    timeout: parseTimeout(details.timeout),
                    filename: details.filename,
                });
                setStatus('revealed');
                return;
            }
            if (!details.secret) {
                throw new Error('No secret content received');
            }
            const decrypted = await decryptSecret(details.secret, urlParams.randomWord);
            const content = parseSecretContent(decrypted, secretType);
            // The server's flag decides; the link's mark counts too, so neither alone shows the password.
            const signIn =
                secretType === SECRET_TYPES.CREDENTIALS && (details.autofill || urlParams.signIn)
                    ? { submit: details.autofill?.submit !== false }
                    : undefined;
            setDecryptedSecret({
                type: secretType,
                content,
                ...(signIn ? { signIn } : {}),
                publicDescription: details.publicDescription,
                timeout: parseTimeout(details.timeout),
                // The form a `schema` secret was answered on, sent alongside it: without carrying
                // it through, its answers would show as raw JSON.
                schema: details.schema,
            });
            setStatus('revealed');
        } catch (decryptionError) {
            const errorMessage =
                decryptionError instanceof Error && decryptionError.message
                    ? decryptionError.message
                    : 'Failed to decrypt secret.';
            setServerError(errorMessage);
            setStatus('error');
        } finally {
            setIsDecrypting(false);
        }
    }, [urlParams, data]);
    /**
     * The file itself, decrypted here. Saving it and playing it are the same fetch and the same
     * decryption; only what happens to the bytes afterwards differs, so both start here.
     */
    const decryptedFile = React.useCallback(async (): Promise<ArrayBuffer | null> => {
        if (!urlParams || !decryptedSecret || decryptedSecret.type !== SECRET_TYPES.FILE) {
            return null;
        }
        try {
            setServerError(null);
            const downloadUrl = decryptedSecret.content;
            if (typeof downloadUrl !== 'string') {
                throw new Error('Invalid download URL');
            }
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error('Failed to download file');
            }
            return await decryptFile(await response.arrayBuffer(), urlParams.randomWord);
        } catch (fileError) {
            const errorMessage =
                fileError instanceof Error && fileError.message ? fileError.message : 'Failed to download file.';
            setServerError(errorMessage);
            throw fileError;
        }
    }, [urlParams, decryptedSecret]);

    const downloadFile = React.useCallback(async (): Promise<void> => {
        const data = await decryptedFile();
        if (data) {
            downloadBlob(data, decryptedSecret?.filename ?? 'download');
        }
    }, [decryptedFile, decryptedSecret]);
    return {
        status,
        decryptedSecret,
        isLoading: isLoading || isFetching,
        isDecrypting,
        pinError,
        serverError,
        pinType,
        urlParams,
        emailVerification,
        emailGuess,
        apiResponse: data,
        error,
        handleView,
        submitPin,
        submitEmailGuess,
        submitEmailCode,
        revealSecret,
        downloadFile,
        decryptedFile,
    };
};

const parseTimeout = (timeout: string | number | undefined): number | undefined => {
    if (typeof timeout === 'number') {
        return timeout;
    }
    if (typeof timeout === 'string') {
        const parsedTimeout = parseInt(timeout, 10);
        return Number.isNaN(parsedTimeout) ? undefined : parsedTimeout;
    }
    return undefined;
};

const parseSecretContent = (
    decrypted: string,
    secretType: string
): string | CredentialsData | Record<string, unknown> => {
    // A form's answers travel as JSON too, one entry per field of the schema.
    if (
        secretType === SECRET_TYPES.CREDENTIALS ||
        secretType === SECRET_TYPES.CUSTOM ||
        secretType === SECRET_TYPES.SCHEMA
    ) {
        try {
            return JSON.parse(decrypted);
        } catch {
            return decrypted;
        }
    }
    return decrypted;
};

const downloadBlob = (data: ArrayBuffer, filename: string): void => {
    const blob = new Blob([data]);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};
